/**
 * ============================================================================
 * DATA LAYER UTILITY CLASS
 * ============================================================================
 */
class DataLayerUtility {
  /**
   * Push to dataLayer with ecommerce reset
   */
  static pushToDataLayer(data) {
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push(data);
  }

  /**
   * Get product data from DOM
   */
  static getProductData() {
    const productElement = document.querySelector('[data-product]');
    if (!productElement) {
      return null;
    }

    try {
      return JSON.parse(productElement.innerHTML);
    } catch (e) {
      console.error('DataLayer: Failed to parse product data', e);
      return null;
    }
  }

  /**
   * Get selected variant data from DOM
   */
  static getSelectedVariantData() {
    const variantElement = document.querySelector('[data-selected-variant]');
    if (!variantElement) {
      return null;
    }

    try {
      return JSON.parse(variantElement.innerHTML);
    } catch (e) {
      console.error('DataLayer: Failed to parse variant data', e);
      return null;
    }
  }

  /**
   * Get list context from section element or URL
   */
  static getListContext(element) {
    const section = element.closest('section, [data-section-type], [data-item-list-id]');

    if (section) {
      // Prefer explicit data attributes
      if (section.dataset.itemListId && section.dataset.itemListName) {
        return {
          id: section.dataset.itemListId,
          name: section.dataset.itemListName,
        };
      }

      // Fallback to section-based detection
      const sectionId = section.id || section.dataset.sectionId || 'unknown';
      const sectionType = section.dataset.sectionType || section.className.match(/section-([^\s]+)/)?.[1] || 'product_list';

      return {
        id: sectionId,
        name: sectionType
          .replace(/_/g, ' ')
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase()),
      };
    }

    // Check if on collection page
    if (window.location.pathname.includes('/collections/')) {
      const collectionMatch = window.location.pathname.match(/\/collections\/([^\/\?#]+)/);
      const collectionHandle = collectionMatch ? collectionMatch[1] : 'unknown';
      return {
        id: `collection_${collectionHandle}`,
        name: 'Collection',
      };
    }

    // Check for homepage
    if (window.location.pathname === '/') {
      return {
        id: 'homepage',
        name: 'Homepage',
      };
    }

    return {
      id: 'unknown',
      name: 'Product List',
    };
  }

  /**
   * Text of a label as a shopper reads it: aria-hidden decoration (accordion numbers, icons) dropped and whitespace
   * collapsed. Text nodes are joined with a space so lines split by <br> or by block spans (the homepage motion
   * script rewrites heading lines into spans) don't run together.
   * @param {Element} element
   * @returns {string}
   */
  static getLabelText(element) {
    const clone = element.cloneNode(true);

    for (const decoration of clone.querySelectorAll('[aria-hidden="true"], svg')) {
      decoration.remove();
    }

    const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
    const parts = [];
    while (walker.nextNode()) {
      parts.push(walker.currentNode.data);
    }

    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  /**
   * Active presentment currency, as every ecommerce payload reports it.
   * @returns {string} ISO currency code
   */
  static getCurrency() {
    return window.Shopify?.currency?.active || 'USD';
  }

  /**
   * Product list containers that hold [data-product-item-data] cards: the
   * collection grid, search results, featured collection and recommendation
   * carousels/grids, shop-the-look and the landing-page bestsellers track.
   * The first three are the original generic selectors.
   * @returns {string[]} CSS selectors
   */
  static get productListSelectors() {
    return [
      '.product-grid',
      'ul.grid',
      '[data-product-list]',
      '[data-collection-grid]',
      '.search__grid',
      '[data-product-grid]',
      '.related-products__grid',
      '.related-products__carousel',
      '.product-recommendations-list',
      '.shop-the-look__products',
      '.lp-bs__track',
    ];
  }

  /**
   * GA4 item from a Cart AJAX API line item (/cart/add.js response line or a
   * cart.items entry). Price is the line's final (discounted) unit price.
   * @param {Object} line - Shopify cart line item
   * @returns {Object} GA4 item with purchase_type and selling_plan_id
   */
  static formatCartLineItem(line) {
    const sellingPlanId = line.selling_plan_allocation?.selling_plan?.id || null;
    const variantId = line.variant_id ?? line.id;

    return {
      item_id: line.sku || variantId?.toString() || 'unknown',
      item_name: line.product_title || '',
      item_brand: line.vendor || 'Bare Performance Nutrition',
      item_category: line.product_type || '',
      item_variant: line.variant_title || '',
      price: (line.final_price ?? line.price ?? 0) / 100,
      quantity: line.quantity || 1,
      purchase_type: sellingPlanId ? 'subscription' : 'one-time',
      selling_plan_id: sellingPlanId,
    };
  }

  /**
   * Lines the shopper did not choose: the threshold gift
   * (properties._gift_threshold, or the legacy _isFreeGift flag) and the
   * SavedBy package-protection fee line (window.Cart.isProtectionLine).
   * @param {Object} line - Shopify cart line item
   * @returns {boolean}
   */
  static isUntrackedCartLine(line) {
    const properties = line?.properties || {};
    const isFlagged = (value) => value === true || value === 'true';

    if (isFlagged(properties._gift_threshold) || isFlagged(properties._isFreeGift)) {
      return true;
    }

    return typeof window.Cart?.isProtectionLine === 'function' ? window.Cart.isProtectionLine(line) : line?.vendor === 'SavedBy';
  }
}

/**
 * ============================================================================
 * DATA LAYER PARENT COMPONENT
 * ============================================================================
 */
class DataLayer extends HTMLElement {
  connectedCallback() {
    // Initialize global dataLayer array once when component mounts
    window.dataLayer = window.dataLayer || [];
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: SELECT ITEM
 * ============================================================================
 */
class DataLayerSelectItem extends HTMLElement {
  constructor() {
    super();
    this.onProductClick = this.onProductClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onProductClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onProductClick);
  }

  onProductClick(event) {
    const productCard = event.target.closest('product-card, .product-card');
    if (!productCard) {
      return;
    }

    // Get the link to the product page to check if it's a product page to trigger select_item
    const link = event.target.closest('a[href*="/products/"]');
    if (!link) {
      return;
    }

    // Skip quick-buy buttons (they trigger add_to_cart instead of select_item)
    if (link.closest('.quick-add') || link.closest('form')) {
      return;
    }

    const dataElement = productCard.querySelector('[data-product-item-data]');
    if (!dataElement) {
      return;
    }

    // Once per card per page
    if (productCard.hasAttribute('data-gtm-select-tracked')) {
      return;
    }

    try {
      const itemData = JSON.parse(dataElement.innerHTML);
      const listContext = DataLayerUtility.getListContext(productCard);
      const index = this.getItemIndex(productCard, dataElement);

      if (index) {
        itemData.index = index;
      }

      productCard.setAttribute('data-gtm-select-tracked', 'true');

      DataLayerUtility.pushToDataLayer({
        event: 'select_item',
        ecommerce: {
          currency: DataLayerUtility.getCurrency(),
          item_list_id: listContext.id,
          item_list_name: listContext.name,
          items: [itemData],
        },
      });
    } catch (e) {
      console.error('DataLayer: Failed to parse product item data', e);
    }
  }

  /**
   * 1-based position of the card in its product list, matching the index
   * view_item_list reports (Swiper loop clones are not counted).
   * @param {HTMLElement} productCard - Clicked card
   * @param {HTMLElement} dataElement - The card's [data-product-item-data] script
   * @returns {number|null}
   */
  getItemIndex(productCard, dataElement) {
    const list = productCard.closest(DataLayerUtility.productListSelectors.join(', '));
    if (!list) {
      return null;
    }

    const cards = Array.from(list.querySelectorAll('[data-product-item-data]')).filter(
      (card) => !card.closest('.swiper-slide-duplicate'),
    );
    const clone = productCard.closest('.swiper-slide-duplicate');
    const position = clone
      ? cards.findIndex((card) => card.textContent === dataElement.textContent)
      : cards.indexOf(dataElement);

    return position >= 0 ? position + 1 : null;
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: VIEW ITEM LIST
 * ============================================================================
 */
class DataLayerViewItemList extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.mutationObserver = null;

    // Define selectors in one place to prevent mismatch bugs (shared with select_item)
    this.listSelectors = DataLayerUtility.productListSelectors;
  }

  connectedCallback() {
    this.initializeObserver();
    this.observeProductLists();
    this.setupMutationObserver();
  }

  disconnectedCallback() {
    // Disconnect IntersectionObserver
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Disconnect MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  initializeObserver() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      return;
    }

    // Create IntersectionObserver with 10% threshold
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Check if element is intersecting and hasn't been tracked yet
          if (entry.isIntersecting && !entry.target.hasAttribute('data-gtm-list-viewed')) {
            // Mark as viewed to prevent duplicate tracking
            entry.target.setAttribute('data-gtm-list-viewed', 'true');

            // Track the view_item_list event
            this.trackViewItemList(entry.target);

            // Stop observing this element
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the list is visible
        rootMargin: '0px',
      },
    );
  }

  observeProductLists() {
    if (!this.observer) {
      return;
    }

    // Select all potential product list containers
    const productLists = document.querySelectorAll(this.listSelectors.join(', '));

    productLists.forEach((list) => {
      // Only observe if list has products and hasn't been observed yet
      const hasProducts = list.querySelectorAll('[data-product-item-data]').length > 0;
      const notYetObserved = !list.hasAttribute('data-gtm-observer-attached');

      if (hasProducts && notYetObserved) {
        list.setAttribute('data-gtm-observer-attached', 'true');
        this.observer.observe(list);
      }
    });
  }

  setupMutationObserver() {
    // Check if MutationObserver is supported
    if (!('MutationObserver' in window)) {
      return;
    }

    // Create MutationObserver to watch for dynamically loaded content
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldReobserve = false;

      mutations.forEach((mutation) => {
        // Check if new nodes were added
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            // Check if the added node is an element and contains product lists
            if (node.nodeType === 1) {
              // Check if the added node matches any of the list selectors
              const isMatch = this.listSelectors.some((selector) => node.matches(selector) || node.querySelector(selector));

              // If the added node matches any of the list selectors, re-observe the product lists
              if (isMatch) {
                shouldReobserve = true;
              }
            }
          });
        }
      });

      // Re-scan for product lists if new content was added
      if (shouldReobserve) {
        this.observeProductLists();
      }
    });

    const targetNode = document.getElementById('MainContent') || document.body;

    // Start observing the document for changes
    this.mutationObserver.observe(targetNode, {
      childList: true,
      subtree: true,
    });
  }

  getProductListItems(listElement) {
    // Swiper loop mode clones slides; count each product once, at its real position
    const productCards = Array.from(listElement.querySelectorAll('[data-product-item-data]')).filter(
      (card) => !card.closest('.swiper-slide-duplicate'),
    );
    return productCards
      .map((card, position) => {
        try {
          return { ...JSON.parse(card.innerHTML), index: position + 1 };
        } catch (e) {
          console.error('DataLayer: Failed to parse product item', e);
          return null;
        }
      })
      .filter(Boolean);
  }

  trackViewItemList(listElement) {
    const listContext = DataLayerUtility.getListContext(listElement);

    // Get all product items in the list
    const items = this.getProductListItems(listElement);

    if (items.length === 0) {
      console.error('DataLayer: view_item_list - No valid product data found');
      return;
    }

    // Push to dataLayer
    DataLayerUtility.pushToDataLayer({
      event: 'view_item_list',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        item_list_id: listContext.id,
        item_list_name: listContext.name,
        items: items,
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: VIEW ITEM
 * ============================================================================
 */
class DataLayerViewItem extends HTMLElement {
  connectedCallback() {
    this.trackViewItem();
  }

  formatGA4Item(productData, variantData, quantity = 1, sellingPlanId = null) {
    const item = {
      item_id: variantData.sku || variantData.id.toString(),
      item_name: productData.title,
      item_brand: productData.vendor,
      item_category: productData.type,
      item_variant: variantData.title,
      price: variantData.price / 100,
      quantity: quantity,
    };

    if (sellingPlanId) {
      item.selling_plan_id = sellingPlanId;
      item.purchase_type = 'subscription';
    } else {
      item.purchase_type = 'one-time';
    }

    return item;
  }

  trackViewItem() {
    const productData = DataLayerUtility.getProductData();
    const variantData = DataLayerUtility.getSelectedVariantData();

    if (!productData || !variantData) {
      console.error('DataLayer: view_item - Missing product or variant data');
      return;
    }

    const item = this.formatGA4Item(productData, variantData, 1);

    DataLayerUtility.pushToDataLayer({
      event: 'view_item',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: item.price,
        items: [item],
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: ADD TO CART
 * ============================================================================
 */
class DataLayerAddToCart extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartRequestEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartRequestEnd);
  }

  onCartRequestEnd(event) {
    const detail = event.detail || {};

    // Only track successful add requests
    if (detail.action !== 'add' || !detail.response) {
      return;
    }

    // FormData adds respond with the added line item at the top level. Cart.addMany
    // responds with the last line at the top level too, plus every line in `items`.
    const lines = Array.isArray(detail.response.items) ? detail.response.items : [detail.response];

    // Same item shape as the previous theme: final (discounted) line price,
    // purchase_type and selling_plan_id on every item
    const items = lines.map((line) => DataLayerUtility.formatCartLineItem(line));

    const cartValue = items.reduce((total, item) => total + item.price * item.quantity, 0);

    DataLayerUtility.pushToDataLayer({
      event: 'add_to_cart',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: cartValue,
        cart_value_text: `${cartValue.toFixed(2)}`,
        items,
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: CTA CLICK
 * ============================================================================
 */
/**
 * Only elements marked `data-track-cta` in Liquid count as CTAs (nav, hero,
 * banner, footer promo, cart empty-state and button-block links), the same
 * allowlist approach as the previous theme. Matching any button would also
 * catch quantity steppers, slider arrows, notices and wrappers whose class
 * merely contains "button".
 */
class DataLayerCtaClick extends HTMLElement {
  constructor() {
    super();
    this.onButtonClick = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onButtonClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onButtonClick);
  }

  onButtonClick(event) {
    const cta = event.target.closest('[data-track-cta]');
    if (!cta) {
      return;
    }

    DataLayerUtility.pushToDataLayer({
      event: 'cta_click',
      // App-store badges are image-only links
      link_text: DataLayerUtility.getLabelText(cta) || cta.getAttribute('aria-label') || cta.querySelector('img[alt]')?.alt || '',
      link_url: cta.href || '',
      cta_location: this.getCtaLocation(cta),
    });
  }

  /**
   * "header" for the header group (nav and mega menu), otherwise the nearest
   * heading inside the CTA's section, as the previous theme reported, then
   * the section id.
   * @param {Element} cta
   * @returns {string}
   */
  getCtaLocation(cta) {
    if (cta.closest('.shopify-section-group-header-group')) {
      return 'header';
    }

    const section = cta.closest('.shopify-section');
    let element = cta.parentElement;

    while (element) {
      const headingElement = element.querySelector('h1, h2, h3, h4, h5, h6');
      const heading = headingElement ? DataLayerUtility.getLabelText(headingElement) : '';
      if (heading) {
        return heading;
      }

      if (element === section) {
        break;
      }

      element = element.parentElement;
    }

    return section?.id || 'page_content';
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: FAQ TOGGLE
 * ============================================================================
 */
/**
 * Only <details data-track-faq> count: the FAQ sections, collection-footer and
 * cart FAQs and the PDP accordions (the previous theme tracked its PDP
 * information accordion too). Filters, footer menus and the share button are
 * also <details> and are not questions.
 */
class DataLayerFaqToggle extends HTMLElement {
  constructor() {
    super();
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.trackedQuestions = new Set();
  }

  connectedCallback() {
    document.addEventListener('click', this.onSummaryClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onSummaryClick, true);
  }

  onSummaryClick(event) {
    const summary = event.target.closest('summary');
    if (!summary) {
      return;
    }

    const details = summary.parentElement;
    if (!details?.matches('details[data-track-faq]')) {
      return;
    }

    // Capture phase runs before the toggle: an open <details> is being closed
    if (details.open) {
      return;
    }

    const faqQuestion = DataLayerUtility.getLabelText(summary) || 'FAQ';

    // First open of each question per page only
    if (this.trackedQuestions.has(faqQuestion)) {
      return;
    }
    this.trackedQuestions.add(faqQuestion);

    DataLayerUtility.pushToDataLayer({
      event: 'faq_toggle',
      faq_question: faqQuestion,
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: ERROR 404
 * ============================================================================
 */
class DataLayerError404 extends HTMLElement {
  connectedCallback() {
    this.track404();
  }

  track404() {
    DataLayerUtility.pushToDataLayer({
      event: 'error_404',
      page_referrer: document.referrer || 'direct',
      page_location: window.location.href,
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: OUT OF STOCK VIEW
 * ============================================================================
 */
class DataLayerOutOfStock extends HTMLElement {
  constructor() {
    super();
    this.onVariantChange = this.onVariantChange.bind(this);
    this.trackedVariants = new Set();
  }

  connectedCallback() {
    this.trackOutOfStock();
    // assets/section-product.js announces every variant switch on the PDP
    document.addEventListener('product:variant-change', this.onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('product:variant-change', this.onVariantChange);
  }

  trackOutOfStock() {
    const productData = DataLayerUtility.getProductData();
    const variantData = DataLayerUtility.getSelectedVariantData();

    if (!productData || !variantData) {
      return;
    }

    if (variantData.available) {
      return;
    }

    this.trackedVariants.add(variantData.id);

    DataLayerUtility.pushToDataLayer({
      event: 'out_of_stock_view',
      item_id: variantData.sku || variantData.id.toString(),
      item_name: productData.title,
    });
  }

  /**
   * A switch to an unavailable variant, once per variant per page.
   * @param {CustomEvent} event - product:variant-change, detail { sectionId, variant }
   */
  onVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant || variant.available || this.trackedVariants.has(variant.id)) {
      return;
    }

    this.trackedVariants.add(variant.id);
    const productData = DataLayerUtility.getProductData();

    DataLayerUtility.pushToDataLayer({
      event: 'out_of_stock_view',
      item_id: variant.sku || variant.id?.toString() || 'unknown',
      item_name: productData?.title || '',
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: VIEW CART
 * ============================================================================
 */
class DataLayerViewCart extends HTMLElement {
  constructor() {
    super();
    this.onCartClick = this.onCartClick.bind(this);
    this.onDrawerClassChange = this.onDrawerClassChange.bind(this);
    this.onCartSettled = this.onCartSettled.bind(this);
    this.drawer = null;
    this.drawerObserver = null;
    this.drawerOpen = false;
    this.openTimer = null;
  }

  /**
   * <cart-drawer> (assets/component-cart-drawer.js) opens by adding .cart-open
   * and dispatches no event, so the drawer's class is the open hook: it covers
   * the header bubble, the auto-open after an add and any other opener. The
   * click heuristic is kept only for pages without a drawer.
   */
  connectedCallback() {
    this.drawer = document.querySelector('cart-drawer');

    if (this.drawer && 'MutationObserver' in window) {
      this.drawerOpen = this.drawer.classList.contains('cart-open');
      this.drawerObserver = new MutationObserver(this.onDrawerClassChange);
      this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      return;
    }

    document.addEventListener('click', this.onCartClick);
  }

  disconnectedCallback() {
    this.drawerObserver?.disconnect();
    this.drawerObserver = null;
    clearTimeout(this.openTimer);
    document.removeEventListener('click', this.onCartClick);
    document.removeEventListener('cart:change', this.onCartSettled);
    document.removeEventListener('cart:error', this.onCartSettled);
  }

  onCartClick(event) {
    const cartTrigger = event.target.closest('[data-cart-bubble], [data-cart-trigger], .cart-trigger');
    if (cartTrigger) {
      setTimeout(this.trackViewCart.bind(this), 100);
    }
  }

  onDrawerClassChange() {
    const isOpen = this.drawer.classList.contains('cart-open');
    const justOpened = isOpen && !this.drawerOpen;
    this.drawerOpen = isOpen;

    if (!justOpened) {
      return;
    }

    // The drawer opens on add-form submit, before the request: report the cart
    // once any pending cart request has landed.
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      if (document.documentElement.classList.contains('cart-busy')) {
        document.addEventListener('cart:change', this.onCartSettled);
        document.addEventListener('cart:error', this.onCartSettled);
        return;
      }

      this.trackViewCart();
    }, 200);
  }

  onCartSettled() {
    document.removeEventListener('cart:change', this.onCartSettled);
    document.removeEventListener('cart:error', this.onCartSettled);
    this.trackViewCart();
  }

  formatCartItems(cartState) {
    if (!cartState?.items) {
      return [];
    }

    return cartState.items.map((item) => DataLayerUtility.formatCartLineItem(item));
  }

  trackViewCart() {
    const cartState = window.Cart?.state;

    if (!cartState || !cartState.items || cartState.item_count === 0) {
      return;
    }

    const items = this.formatCartItems(cartState);
    const cartValue = cartState.total_price / 100;

    DataLayerUtility.pushToDataLayer({
      event: 'view_cart',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: cartValue,
        items: items,
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: REMOVE FROM CART
 * ============================================================================
 */
class DataLayerRemoveFromCart extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartRequestEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartRequestEnd);
  }

  onCartRequestEnd(event) {
    const detail = event.detail || {};

    // Only track change requests that result in removals
    if (detail.action !== 'change') {
      return;
    }

    // Automatic gift-threshold removals are not shopper removals
    if (detail.source === 'gift-threshold') {
      return;
    }

    // assets/cart.js sends the cart before (previousCart) and after (response)
    // the change; a removed line is one that is no longer in the cart.
    const previousItems = detail.previousCart?.items;
    const currentCartItems = detail.response?.items;

    if (!Array.isArray(previousItems) || !Array.isArray(currentCartItems)) {
      return;
    }

    const completelyRemovedItems = this.findRemovedLines(previousItems, currentCartItems);

    if (completelyRemovedItems.length > 0) {
      this.trackRemoveFromCart(completelyRemovedItems);
    }
  }

  /**
   * Previous lines missing from the current cart, matched by line key and then
   * by variant id (a selling-plan switch re-keys a line without removing it).
   * Gift and package-protection lines are left out, as before.
   * @param {Object[]} previousItems - cart.items before the change
   * @param {Object[]} currentCartItems - cart.items after the change
   * @returns {Object[]} Removed line items (previous snapshot)
   */
  findRemovedLines(previousItems, currentCartItems) {
    const previousKeys = new Set(previousItems.map((item) => item.key));
    const matchedKeys = new Set(currentCartItems.filter((item) => previousKeys.has(item.key)).map((item) => item.key));

    return previousItems.filter((previousItem) => {
      if (matchedKeys.has(previousItem.key)) {
        return false;
      }

      const sameVariant = currentCartItems.find(
        (cartItem) => !matchedKeys.has(cartItem.key) && cartItem.variant_id === previousItem.variant_id,
      );
      if (sameVariant) {
        matchedKeys.add(sameVariant.key);
        return false;
      }

      return !DataLayerUtility.isUntrackedCartLine(previousItem);
    });
  }

  trackRemoveFromCart(removedItems) {
    const itemsForGA4 = removedItems.map((item) => DataLayerUtility.formatCartLineItem(item));
    const totalRemovedValue = itemsForGA4.reduce((total, item) => total + item.price * item.quantity, 0);

    DataLayerUtility.pushToDataLayer({
      event: 'remove_from_cart',
      ecommerce: {
        currency: window.Shopify?.currency?.active || 'USD',
        value: totalRemovedValue,
        items: itemsForGA4,
      },
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: UPDATE CART
 * ============================================================================
 */
class DataLayerUpdateCart extends HTMLElement {
  constructor() {
    super();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartRequestEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartRequestEnd);
  }

  onCartRequestEnd(event) {
    const detail = event.detail || {};

    // Only track change requests
    if (detail.action !== 'change') {
      return;
    }

    // Get updated items from response and the snapshot taken before the mutation
    const updatedItems = detail.response?.items;
    const previousItems = detail.previousCart?.items;

    // If there are no updated items or the previous cart state is not available, return
    if (!updatedItems || !previousItems) {
      return;
    }

    // Automatic gift-threshold adjustments are not shopper updates
    if (detail.source === 'gift-threshold') {
      return;
    }

    // Compare previous cart state with current state to find quantity changes
    updatedItems.forEach((currentItem) => {
      // Gift and package-protection lines are not tracked (as before)
      if (DataLayerUtility.isUntrackedCartLine(currentItem)) {
        return;
      }

      // A line's key survives quantity changes; the variant alone does not identify it, since the same variant can
      // sit in the cart as both a one-time and a subscription line
      const previousItem = previousItems.find((prev) => prev.key === currentItem.key);

      // If item exists in previous state and quantity changed
      if (previousItem && previousItem.quantity !== currentItem.quantity) {
        DataLayerUtility.pushToDataLayer({
          event: 'update_cart',
          item_id: currentItem.sku || currentItem.variant_id?.toString(),
          item_name: currentItem.product_title,
          previous_quantity: previousItem.quantity,
          new_quantity: currentItem.quantity,
        });
      }
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: SUBSCRIPTION UPSELL
 * ============================================================================
 */
class DataLayerSubscriptionUpsell extends HTMLElement {
  constructor() {
    super();
    this.onCartChange = this.onCartChange.bind(this);
    this.lastTracked = { variantId: null, time: 0 };
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
  }

  /**
   * assets/component-cart-purchase-mode.js (cart drawer and cart page) moves a
   * line onto a selling plan with Cart.change({ line, quantity, selling_plan }).
   * A one-time line gaining a plan is the upsell; frequency changes are not.
   * @param {CustomEvent} event - cart:change
   */
  onCartChange(event) {
    try {
      const detail = event.detail || {};
      if (detail.action !== 'change' || !detail.payload?.selling_plan) {
        return;
      }

      const previousLine = detail.previousCart?.items?.[Number(detail.payload.line) - 1];
      if (!previousLine || previousLine.selling_plan_allocation || DataLayerUtility.isUntrackedCartLine(previousLine)) {
        return;
      }

      const sellingPlanId = String(detail.payload.selling_plan);
      const currentLine = detail.response?.items?.find(
        (item) => item.variant_id === previousLine.variant_id && String(item.selling_plan_allocation?.selling_plan?.id) === sellingPlanId,
      );
      if (!currentLine || this.isDuplicate(currentLine.variant_id)) {
        return;
      }

      const item = DataLayerUtility.formatCartLineItem(currentLine);

      DataLayerUtility.pushToDataLayer({
        event: 'subscription_upsell',
        item_id: item.item_id,
        item_name: item.item_name,
        upsell_location: this.getUpsellLocation(detail.source),
        ecommerce: {
          currency: DataLayerUtility.getCurrency(),
          value: item.price * item.quantity,
          items: [item],
        },
      });
    } catch (error) {
      console.error('DataLayer: subscription_upsell failed', error);
    }
  }

  /**
   * Same variant within 5 seconds counts once.
   * @param {number} variantId
   * @returns {boolean}
   */
  isDuplicate(variantId) {
    const now = Date.now();
    const duplicate = this.lastTracked.variantId === variantId && now - this.lastTracked.time < 5000;
    this.lastTracked = { variantId, time: now };
    return duplicate;
  }

  /**
   * @param {Element|string|null} source - cart:change detail.source
   * @returns {string} 'cart_page' on the cart template, otherwise 'cart_drawer'
   */
  getUpsellLocation(source) {
    return source instanceof Element && source.closest('cart-page') ? 'cart_page' : 'cart_drawer';
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: PRODUCT OPTION SELECTED
 * ============================================================================
 */
class DataLayerProductOption extends HTMLElement {
  /** A cart swap retries its removal for a few seconds; a pick older than this never completed */
  static CART_SWAP_TIMEOUT_MS = 20000;

  constructor() {
    super();
    this.onOptionChange = this.onOptionChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onCartSwapClick = this.onCartSwapClick.bind(this);
    this.onCartRefresh = this.onCartRefresh.bind(this);
    this.lastTracked = { type: '', value: '', time: 0 };
    this.pendingOption = null;
    this.pendingCartSwap = null;
  }

  connectedCallback() {
    document.addEventListener('change', this.onOptionChange);
    document.addEventListener('product:variant-change', this.onVariantChange);
    document.addEventListener('click', this.onCartSwapClick);
    document.addEventListener('cart:change', this.onCartRefresh);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onOptionChange);
    document.removeEventListener('product:variant-change', this.onVariantChange);
    document.removeEventListener('click', this.onCartSwapClick);
    document.removeEventListener('cart:change', this.onCartRefresh);
  }

  onOptionChange(event) {
    // Subscribe / one-time and delivery frequency (snippets/component-selling-plans.liquid)
    if (event.target.closest('selling-plans-widget')) {
      this.onSellingPlanChange(event.target);
      return;
    }

    // Check if change event is from variant-selector
    const variantSelector = event.target.closest('variant-selector');
    if (!variantSelector) {
      return;
    }

    // Determine picker type and extract option data
    const pickerType = variantSelector.dataset.pickerType;
    let optionName, optionValue;

    if (pickerType === 'button') {
      // Extract from radio input
      const input = event.target;
      if (input.type !== 'radio' || !input.checked) {
        return;
      }

      const fieldset = input.closest('fieldset');
      const legend = fieldset?.querySelector('legend');
      optionName = legend?.textContent?.trim() || '';
      optionValue = input.value;
    } else {
      // Image, swatch rail and dropdown pickers carry the option name in the input name
      ({ name: optionName, value: optionValue } = this.getOptionFromInput(event.target));
    }

    if (!optionName || !optionValue) {
      return;
    }

    // The PDP re-renders for the new variant (assets/section-product.js); until then the page still holds the
    // previous variant, so report once product:variant-change carries the new one. A newer pick replaces this one.
    this.pendingOption = { optionName, optionType: this.toOptionType(optionName), optionValue };
  }

  /** @param {CustomEvent} event - product:variant-change, detail.variant is null for an unavailable combination */
  onVariantChange(event) {
    if (!this.pendingOption) {
      return;
    }

    const { optionName, optionType, optionValue } = this.pendingOption;
    this.pendingOption = null;
    this.pushOption(optionName, optionType, optionValue, event.detail?.variant ?? null);
  }

  /**
   * Option name/value from a variant picker input: radios are named
   * "<option name>-<position>", dropdown selects "options[<option name>]".
   * @param {HTMLInputElement|HTMLSelectElement} input
   * @returns {{name: string, value: string}}
   */
  getOptionFromInput(input) {
    if (input.type === 'radio') {
      return input.checked ? { name: input.name.replace(/-\d+$/, ''), value: input.value } : {};
    }

    if (input.tagName === 'SELECT') {
      return { name: input.name.match(/^options\[(.+)\]$/)?.[1] || '', value: input.value };
    }

    return {};
  }

  /**
   * purchase_type ('subscription' | 'one-time') from the purchase radios and
   * delivery_frequency (the visible "Every …" label) from the plan select.
   * @param {Element} target - Changed control inside <selling-plans-widget>
   */
  onSellingPlanChange(target) {
    if (target.matches('input[type="radio"][data-radio-type]')) {
      if (!target.checked) {
        return;
      }

      const purchaseType = target.dataset.radioType === 'selling_plan' ? 'subscription' : 'one-time';
      this.pushOption('purchase_type', 'purchase_type', purchaseType);
      return;
    }

    if (target.matches('[data-selling-plan-select]')) {
      const axis = target.closest('.product-sub__frequency-wrap');
      const option = axis?.querySelector(`[data-frequency-option][data-value="${CSS.escape(target.value)}"]`);
      const frequency = option?.textContent?.trim() || target.selectedOptions?.[0]?.textContent?.trim() || target.value;
      this.pushOption('delivery_frequency', 'delivery_frequency', frequency);
    }
  }

  /**
   * GTM's option_type variable reads a snake_case type ('flavor', 'size', …).
   * @param {string} optionName - Shopify option name, e.g. "Flavor"
   * @returns {string}
   */
  toOptionType(optionName) {
    return optionName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  /**
   * Cart page "Change" panel (snippets/component-cart-item-card.liquid). component-cart-page.js swaps the line with
   * its own add and remove requests, then Cart.refresh(), so no cart:change names the option. Note the line as it was
   * when the shopper picked (the same checks as the page's handler), and compare it with its replacement once the
   * refresh lands.
   * @param {MouseEvent} event
   */
  onCartSwapClick(event) {
    const option = event.target.closest('.cart-variant-panel__option');
    if (!option || option.disabled || option.getAttribute('aria-pressed') === 'true') {
      return;
    }

    const items = window.Cart?.state?.items || [];
    const lineKey = option.closest('.cart-line')?.dataset.lineKey;
    const oldLine = items.find((item) => item.key === lineKey);
    if (!oldLine) {
      return;
    }

    this.pendingCartSwap = {
      oldLine,
      quantitiesBefore: new Map(items.map((item) => [item.key, item.quantity])),
      time: Date.now(),
    };
  }

  /** @param {CustomEvent} event - cart:change; the swap's closing refresh carries the replacement line */
  onCartRefresh(event) {
    const swap = this.pendingCartSwap;
    if (!swap || event.detail?.action !== 'refresh') {
      return;
    }

    if (Date.now() - swap.time > DataLayerProductOption.CART_SWAP_TIMEOUT_MS) {
      this.pendingCartSwap = null;
      return;
    }

    // The old line is still there while the swap runs, or when its removal failed and the shopper holds both lines
    const items = event.detail.cart?.items || [];
    if (items.some((item) => item.key === swap.oldLine.key)) {
      return;
    }

    this.pendingCartSwap = null;

    // A new line for another variant of the same product, or an existing one it merged into
    const candidates = items.filter(
      (item) =>
        item.product_id === swap.oldLine.product_id &&
        item.variant_id !== swap.oldLine.variant_id &&
        !DataLayerUtility.isUntrackedCartLine(item),
    );
    const newLine =
      candidates.find((item) => !swap.quantitiesBefore.has(item.key)) ||
      candidates.find((item) => item.quantity > swap.quantitiesBefore.get(item.key));
    if (!newLine) {
      return;
    }

    const previousOptions = swap.oldLine.options_with_values || [];
    for (const { name, value } of newLine.options_with_values || []) {
      if (previousOptions.find((option) => option.name === name)?.value === value) {
        continue;
      }

      this.pushOption(
        name,
        this.toOptionType(name),
        value,
        { id: newLine.variant_id, sku: newLine.sku },
        { id: newLine.product_id, title: newLine.product_title },
      );
    }
  }

  /**
   * @param {string} optionName
   * @param {string} optionType
   * @param {string} optionValue
   * @param {Object|null} [variant] - defaults to the selected variant on the page (selling-plan changes keep it)
   * @param {{ id: number|string, title: string }|null} [product] - defaults to the PDP's product
   */
  pushOption(
    optionName,
    optionType,
    optionValue,
    variant = DataLayerUtility.getSelectedVariantData(),
    product = DataLayerUtility.getProductData(),
  ) {
    if (!optionValue) {
      return;
    }

    // Same choice within 300 ms (e.g. a mirrored control) counts once
    const now = Date.now();
    const last = this.lastTracked;
    if (last.type === optionType && last.value === optionValue && now - last.time < 300) {
      return;
    }
    this.lastTracked = { type: optionType, value: optionValue, time: now };

    if (!product) {
      return;
    }

    // Push to data layer
    DataLayerUtility.pushToDataLayer({
      event: 'product_option_selected',
      option_type: optionType,
      option_name: optionName,
      option_value: optionValue,
      product_id: product.id?.toString(),
      product_name: product.title,
      variant_id: variant?.id?.toString() || '',
      variant_sku: variant?.sku || '',
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: SEARCH
 * ============================================================================
 */
class DataLayerSearch extends HTMLElement {
  constructor() {
    super();
    this.onSearchSubmit = this.onSearchSubmit.bind(this);
  }

  connectedCallback() {
    // Track search form submissions
    document.addEventListener('submit', this.onSearchSubmit);
    // The search drawer, search page and masthead forms stop their submit event
    // (assets/section-search-drawer.js) and navigate to the results page, so
    // the results page load is tracked too unless a submit already reported it.
    this.trackResultsPage();
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onSearchSubmit);
  }

  onSearchSubmit(event) {
    const form = event.target;

    // Check if it's a search form
    if (!form.action?.includes('/search') && !form.querySelector('input[name="q"]') && !form.classList.contains('search')) {
      return;
    }

    const searchInput = form.querySelector('input[name="q"], input[type="search"]');
    if (!searchInput) {
      return;
    }

    const searchTerm = searchInput.value?.trim();
    if (searchTerm) {
      DataLayerUtility.pushToDataLayer({
        event: 'search',
        search_term: searchTerm,
      });

      try {
        sessionStorage.setItem('search_tracked_term', searchTerm);
      } catch (error) {
        console.warn('DataLayer: search - could not store the tracked term', error);
      }
    }
  }

  /**
   * search on a results page load (?q=), skipped when the submit that led
   * here already pushed the same term — one event per search.
   */
  trackResultsPage() {
    if (!/\/search\/?$/.test(window.location.pathname)) {
      return;
    }

    const searchTerm = new URLSearchParams(window.location.search).get('q')?.trim();
    if (!searchTerm) {
      return;
    }

    let alreadyTracked = false;
    try {
      alreadyTracked = sessionStorage.getItem('search_tracked_term') === searchTerm;
      sessionStorage.removeItem('search_tracked_term');
    } catch (error) {
      console.warn('DataLayer: search - could not read the tracked term', error);
    }

    if (alreadyTracked) {
      return;
    }

    DataLayerUtility.pushToDataLayer({
      event: 'search',
      search_term: searchTerm,
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: CAROUSEL INTERACTION
 * ============================================================================
 */
class DataLayerCarousel extends HTMLElement {
  constructor() {
    super();
    this.onCarouselClick = this.onCarouselClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onCarouselClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onCarouselClick);
  }

  onCarouselClick(event) {
    // Swiper navigation arrows: div[role="button"] or <button> (e.g. related products), and the
    // <swipper-slider> sections' arrows (card, video, reviews, community, PDP media carousels)
    const button = event.target.closest('.swiper-button-next, .swiper-button-prev, .swiper-slider__next, .swiper-slider__prev');
    if (!button) {
      return;
    }

    const isPrev = button.matches('.swiper-button-prev, .swiper-slider__prev');

    // Arrows sit inside the swiper container or beside it; most containers have no id, so fall back to the section
    const swiperContainer = button.closest('.swiper') || button.parentElement?.querySelector('.swiper');
    const carouselId =
      swiperContainer?.id || swiperContainer?.dataset.carouselId || button.closest('.shopify-section, section')?.id || 'swiper';
    const slideDirection = isPrev ? 'previous' : 'next';

    DataLayerUtility.pushToDataLayer({
      event: 'carousel_interaction',
      interaction_type: 'arrow_click',
      slide_direction: slideDirection,
      carousel_id: carouselId,
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: CHAT INITIATED
 * ============================================================================
 */
class DataLayerChatInitiated extends HTMLElement {
  constructor() {
    super();
    this.onWidgetLoaded = this.onWidgetLoaded.bind(this);
    this.trackChat = this.trackChat.bind(this);
    this.listening = false;
    this.tracked = false;
  }

  /**
   * Gorgias chat comes from its app embed. Until window.GorgiasChat exists
   * this element only waits for the widget's 'gorgias-widget-loaded' event.
   */
  connectedCallback() {
    if (window.GorgiasChat) {
      this.listenToGorgias();
      return;
    }

    window.addEventListener('gorgias-widget-loaded', this.onWidgetLoaded);
  }

  disconnectedCallback() {
    window.removeEventListener('gorgias-widget-loaded', this.onWidgetLoaded);
  }

  onWidgetLoaded() {
    window.removeEventListener('gorgias-widget-loaded', this.onWidgetLoaded);
    this.listenToGorgias();
  }

  async listenToGorgias() {
    const chat = window.GorgiasChat;
    if (this.listening || typeof chat?.init !== 'function') {
      return;
    }

    this.listening = true;

    try {
      await chat.init();
      if (typeof chat.on === 'function') {
        chat.on('widget:opened', this.trackChat);
      }
    } catch (error) {
      console.warn('DataLayer: chat_initiated - Gorgias chat unavailable', error);
    }
  }

  /** First widget open per page only. */
  trackChat() {
    if (this.tracked) {
      return;
    }

    this.tracked = true;

    DataLayerUtility.pushToDataLayer({
      event: 'chat_initiated',
      chat_location: 'floating_widget',
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: SELECT CONTENT
 * ============================================================================
 */
class DataLayerSelectContent extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onClick);
    document.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('change', this.onChange);
  }

  onClick(event) {
    // Find element with data-track-content-type
    const element = event.target.closest('[data-track-content-type]');
    if (!element) {
      return;
    }

    // Skip select elements (handled by onChange)
    if (element.tagName === 'SELECT') {
      return;
    }

    // A filter counts when it is applied, not when it is cleared
    if (element.type === 'checkbox' && !element.checked) {
      return;
    }

    // Extract content_type and item_id from data attributes
    const contentType = element.dataset.trackContentType;
    const itemId = element.dataset.trackItemId;
    const itemName = element.dataset.trackItemName || element.textContent?.trim();

    // Validate required attributes
    if (!contentType || !itemId) {
      console.warn('DataLayer: select_content - Missing required data attributes');
      return;
    }

    // Push to dataLayer
    DataLayerUtility.pushToDataLayer({
      event: 'select_content',
      content_type: contentType,
      item_id: itemId,
      item_name: itemName,
    });
  }

  onChange(event) {
    // Handle change events on select elements with data-track-content-type="sort"
    const select = event.target.closest('select[data-track-content-type="sort"]');
    if (!select) {
      return;
    }

    const selectedOption = select.options[select.selectedIndex];
    const itemId = selectedOption.dataset.trackItemId || select.value;
    const itemName = selectedOption.dataset.trackItemName || selectedOption.textContent?.trim();

    // Validate required attributes
    if (!itemId) {
      console.warn('DataLayer: select_content - Missing required data attributes on sort select');
      return;
    }

    // Push to dataLayer
    DataLayerUtility.pushToDataLayer({
      event: 'select_content',
      content_type: 'sort',
      item_id: itemId,
      item_name: itemName,
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: LOGIN
 * ============================================================================
 */
class DataLayerLogin extends HTMLElement {
  constructor() {
    super();
    this.handleLoginSubmit = this.handleLoginSubmit.bind(this);
  }

  connectedCallback() {
    this.checkForRecentLogin();

    const loginForm = document.querySelector('form[action*="/account/login"]');
    if (loginForm) {
      loginForm.addEventListener('submit', this.handleLoginSubmit);
    }
  }

  disconnectedCallback() {
    const loginForm = document.querySelector('form[action*="/account/login"]');
    if (loginForm) {
      loginForm.removeEventListener('submit', this.handleLoginSubmit);
    }
  }

  handleLoginSubmit(event) {
    const loginForm = event.target;

    if (!loginForm.action?.includes('/account/login') || !loginForm.querySelector('input[name="customer[email]"]')) {
      return;
    }

    try {
      sessionStorage.setItem('login_provider', 'email');
      sessionStorage.setItem('login_timestamp', new Date().toISOString());
      sessionStorage.setItem('just_logged_in', 'true');
    } catch (error) {
      console.error('DataLayer: Failed to set login tracking flags', error);
    }
  }

  /**
   * data-logged-in is rendered by snippets/component-data-layer.liquid when a
   * customer is signed in, so a login only counts once it has succeeded.
   * method: 'email' after the login form; 'shop_pay' when a shopper who was a
   * guest earlier this session is signed in without it (Sign in with Shop).
   */
  checkForRecentLogin() {
    try {
      const isLoggedIn = this.hasAttribute('data-logged-in');
      const justLoggedIn = sessionStorage.getItem('just_logged_in');
      const loginProvider = sessionStorage.getItem('login_provider') || 'email';
      const wasGuest = sessionStorage.getItem('login_was_guest') === 'true';

      // Contact details are not kept for this event; drop any stored by an earlier version
      sessionStorage.removeItem('login_email');

      if (!isLoggedIn) {
        sessionStorage.setItem('login_was_guest', 'true');
      }

      if (justLoggedIn === 'true') {
        // Clear flags; a failed attempt (still a guest) is not a login
        sessionStorage.removeItem('just_logged_in');
        sessionStorage.removeItem('login_timestamp');
        sessionStorage.removeItem('login_provider');

        if (isLoggedIn) {
          sessionStorage.removeItem('login_was_guest');
          this.handleSuccessfulLogin(loginProvider);
        }
        return;
      }

      if (isLoggedIn && wasGuest) {
        sessionStorage.removeItem('login_was_guest');
        this.handleSuccessfulLogin('shop_pay');
      }
    } catch (error) {
      console.error('DataLayer: Failed to read login tracking flags', error);
    }
  }

  handleSuccessfulLogin(loginProvider) {
    DataLayerUtility.pushToDataLayer({
      event: 'login',
      method: loginProvider || 'email',
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: SIGN UP
 * ============================================================================
 */
class DataLayerSignUp extends HTMLElement {
  constructor() {
    super();
    this.handleSignupSubmit = this.handleSignupSubmit.bind(this);
  }

  connectedCallback() {
    document.addEventListener('submit', this.handleSignupSubmit);
    this.checkForRecentSignup();
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.handleSignupSubmit);
  }

  handleSignupSubmit(event) {
    const form = event.target;

    if (!form.action?.includes('/account') || !form.querySelector('input[name="customer[email]"]')) {
      return;
    }

    // Registration form only ({% form 'create_customer' %}), not login or recover
    if (!form.querySelector('input[name="form_type"][value="create_customer"]')) {
      return;
    }

    if (!form.querySelector('input[name="customer[email]"]').value) {
      return;
    }

    try {
      sessionStorage.setItem('just_signed_up', 'true');
    } catch (error) {
      console.error('DataLayer: Failed to set sign-up tracking flag', error);
    }
  }

  /**
   * Fires on the first page after a registration submit that is not the
   * registration form again (Shopify re-renders it when the sign-up fails).
   */
  checkForRecentSignup() {
    try {
      const justSignedUp = sessionStorage.getItem('just_signed_up');

      // Contact details are not kept for this event; drop any stored by an earlier version
      sessionStorage.removeItem('signup_email');
      sessionStorage.removeItem('signup_first_name');

      if (justSignedUp !== 'true') {
        return;
      }

      if (document.querySelector('input[name="form_type"][value="create_customer"]')) {
        // Still on the registration form: clear the flag only when it shows errors
        if (document.querySelector('.form-message--error, .input-error-message')) {
          sessionStorage.removeItem('just_signed_up');
        }
        return;
      }

      this.handleSuccessfulSignup();

      // Clear flags after tracking
      sessionStorage.removeItem('just_signed_up');
      sessionStorage.removeItem('signup_timestamp');
    } catch (error) {
      console.error('DataLayer: Failed to read sign-up tracking flag', error);
    }
  }

  handleSuccessfulSignup() {
    DataLayerUtility.pushToDataLayer({
      event: 'sign_up',
      method: 'email',
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: GENERATE LEAD
 * ============================================================================
 */
class DataLayerGenerateLead extends HTMLElement {
  constructor() {
    super();
    this.onKlaviyoForm = this.onKlaviyoForm.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
  }

  connectedCallback() {
    // Klaviyo onsite forms announce themselves on window; inert until Klaviyo loads
    window.addEventListener('klaviyoForms', this.onKlaviyoForm);
    document.addEventListener('submit', this.onFormSubmit);
  }

  disconnectedCallback() {
    window.removeEventListener('klaviyoForms', this.onKlaviyoForm);
    document.removeEventListener('submit', this.onFormSubmit);
  }

  /**
   * @param {CustomEvent} event - klaviyoForms, detail { type, formId, metaData }
   */
  onKlaviyoForm(event) {
    const type = event.detail?.type;
    if (type !== 'submit' && type !== 'stepSubmit') {
      return;
    }

    const formId = event.detail?.formId;
    const form = formId ? document.querySelector(`[data-klaviyo-form-id="${CSS.escape(String(formId))}"]`) : null;
    const email = form?.querySelector('input[type="email"]')?.value || event.detail?.metaData?.$email || '';

    this.trackLead(email ? this.emailKey(email) : `klaviyo_form_${formId || 'unknown'}`);
  }

  /**
   * Native newsletter sign-up: {% form 'customer' %} posting contact[tags]=newsletter
   * (snippets/component-footer-newsletter.liquid).
   * @param {SubmitEvent} event
   */
  onFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !(form.getAttribute('action') || '').includes('/contact')) {
      return;
    }

    const isNewsletter =
      form.querySelector('input[name="form_type"][value="customer"]') && form.querySelector('input[name="contact[tags]"][value="newsletter"]');
    const email = form.querySelector('input[type="email"], input[name="contact[email]"]')?.value?.trim();

    if (isNewsletter && email) {
      this.trackLead(this.emailKey(email));
    }
  }

  /**
   * Session de-duplication key for an address, without storing the address.
   * @param {string} email
   * @returns {string} FNV-1a hash of the normalised address
   */
  emailKey(email) {
    let hash = 0x811c9dc5;
    for (const char of email.trim().toLowerCase()) {
      hash ^= char.codePointAt(0);
      hash = Math.imul(hash, 0x01000193);
    }
    return `email_${(hash >>> 0).toString(16)}`;
  }

  /**
   * One generate_lead per sign-up: a repeat of the last key this session is skipped.
   * @param {string} leadKey
   */
  trackLead(leadKey) {
    try {
      if (sessionStorage.getItem('gtm_last_lead') === leadKey) {
        return;
      }
      sessionStorage.setItem('gtm_last_lead', leadKey);
      // The previous theme stored the plain address under this key
      sessionStorage.removeItem('gtm_last_lead_email');
    } catch (error) {
      console.warn('DataLayer: generate_lead - session de-duplication unavailable', error);
    }

    DataLayerUtility.pushToDataLayer({
      event: 'generate_lead',
      currency: DataLayerUtility.getCurrency(),
      value: 0.5,
      lead_type: 'newsletter',
    });
  }
}

/**
 * ============================================================================
 * EVENT COMPONENT: PROMOTIONS (KLAVIYO FORMS)
 * ============================================================================
 */
class DataLayerPromotions extends HTMLElement {
  constructor() {
    super();
    this.onKlaviyoForm = this.onKlaviyoForm.bind(this);
  }

  connectedCallback() {
    // Klaviyo onsite forms announce themselves on window; inert until Klaviyo loads
    window.addEventListener('klaviyoForms', this.onKlaviyoForm);
  }

  disconnectedCallback() {
    window.removeEventListener('klaviyoForms', this.onKlaviyoForm);
  }

  /**
   * Promotion keys are top level, as GTM's promotion tags read them.
   * @param {CustomEvent} event - klaviyoForms, detail { type, formId, metaData }
   */
  onKlaviyoForm(event) {
    const type = event.detail?.type;
    const formId = String(event.detail?.formId || 'unknown');
    const metaData = event.detail?.metaData || {};
    const promotion = {
      promotion_id: `klaviyo_form_${formId}`,
      promotion_name: metaData.$form_name || metaData.formName || 'Klaviyo Popup',
      creative_name: 'klaviyo_popup',
    };

    if (type === 'open') {
      this.trackOnce('view_promotion', 'gtm_viewed_promotions', formId, promotion);
    }

    if (type === 'submit' || type === 'stepSubmit') {
      this.trackOnce('select_promotion', 'gtm_selected_promotions', formId, promotion);
    }
  }

  /**
   * Once per form per session; only Klaviyo form ids are stored.
   * @param {string} eventName
   * @param {string} storageKey - sessionStorage key holding the tracked form ids
   * @param {string} formId
   * @param {Object} promotion - promotion_id, promotion_name, creative_name
   */
  trackOnce(eventName, storageKey, formId, promotion) {
    let tracked = [];
    try {
      const stored = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
      tracked = Array.isArray(stored) ? stored.map(String) : [];
    } catch (error) {
      console.warn('DataLayer: promotions - could not read tracked forms', error);
    }

    if (tracked.includes(formId)) {
      return;
    }

    try {
      sessionStorage.setItem(storageKey, JSON.stringify([...tracked, formId]));
    } catch (error) {
      console.warn('DataLayer: promotions - could not store tracked forms', error);
    }

    DataLayerUtility.pushToDataLayer({
      event: eventName,
      ...promotion,
    });
  }
}

/**
 * ============================================================================
 * COMPONENT REGISTRATION
 * ============================================================================
 */

const dataLayerComponents = {
  'data-layer': DataLayer, // Register parent first
  // Core E-commerce Events
  'data-layer-select-item': DataLayerSelectItem,
  'data-layer-view-item': DataLayerViewItem,
  'data-layer-view-item-list': DataLayerViewItemList,
  'data-layer-add-to-cart': DataLayerAddToCart,
  'data-layer-view-cart': DataLayerViewCart,
  'data-layer-remove-from-cart': DataLayerRemoveFromCart,
  // Cart Operations
  'data-layer-update-cart': DataLayerUpdateCart,
  'data-layer-subscription-upsell': DataLayerSubscriptionUpsell,
  // User Engagement Events
  'data-layer-cta-click': DataLayerCtaClick,
  'data-layer-faq-toggle': DataLayerFaqToggle,
  'data-layer-search': DataLayerSearch,
  'data-layer-select-content': DataLayerSelectContent,
  'data-layer-carousel': DataLayerCarousel,
  'data-layer-chat-initiated': DataLayerChatInitiated,
  'data-layer-product-option': DataLayerProductOption,
  // Authentication Events
  'data-layer-login': DataLayerLogin,
  'data-layer-sign-up': DataLayerSignUp,
  // Marketing Events
  'data-layer-generate-lead': DataLayerGenerateLead,
  'data-layer-promotions': DataLayerPromotions,
  // System & Status Events
  'data-layer-error-404': DataLayerError404,
  'data-layer-out-of-stock': DataLayerOutOfStock,
};

Object.entries(dataLayerComponents).forEach(([tagName, componentClass]) => {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, componentClass);
  }
});
